import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs-pro";
import FileUtils from "../utils/files.utils";
import apiWithAuth from "./apiWithAuth";
import posthog from "posthog-js";

const fpAgentPromise = FingerprintJS.load({
   apiKey: "OJ2Akx4R5t9XB1qFaH37",
   endpoint: "https://metrics.driverify.ai",
   scriptUrlPattern:
      "https://metrics.driverify.ai/web/v<version>/<apiKey>/loader_v<loaderVersion>.js",
});

const api = axios.create({
   headers: {
      "Content-Type": "application/json",
   },
});

export const visitorIdInterceptor = async (config) => {
   try {
      const agent = await fpAgentPromise;
      const { visitorId } = await agent.get();
      if (visitorId) {
         config.headers["X-Visitor-Id"] = visitorId;
      }
   } catch (error) {
      posthog.capture("visitor_id_fetch_error", {
         context: "api_request",
         url: config.url,
         method: config.method,
         error: String(error),
      });
   }

   return config;
};

api.interceptors.request.use(visitorIdInterceptor);

// Dispatch Form APIs
export const dispatchAPI = {
   // Submit dispatch form
   submitForm: async (formData) => {
      const formDataObj = new FormData();
      Object.keys(formData).forEach((key) => {
         if (formData[key] !== null && formData[key] !== undefined) {
            const value =
               typeof formData[key] === "object" ? JSON.stringify(formData[key]) : formData[key];
            formDataObj.append(key, value);
         }
      });

      const response = await api.post("/api/dispatch-form/submit", formDataObj, {
         headers: {
            "Content-Type": "multipart/form-data",
         },
      });
      return response.data;
   },

   // Get dispatch responses
   getResponses: async () => {
      const response = await api.get("/api/responses/dispatch");
      return response.data;
   },

   // Clear dispatch responses
   clearResponses: async () => {
      const response = await api.delete("/api/responses/dispatch");
      return response.data;
   },
};

// Driver Verification APIs
export const driverVerificationAPI = {
   // Submit driver verification form
   submitForm: async (formData) => {
      // Upload files first (if any) to receive public URLs (JSON instead of form-data)
      const filesToUpload = {};
      if (formData.truckPhoto instanceof File) filesToUpload.truckPhoto = formData.truckPhoto;
      if (formData.cabCard instanceof File) filesToUpload.cabCard = formData.cabCard;

      let truckPhotoUrl = formData.truckPhotoUrl || null;
      let cabCardUrl = formData.cabCardUrl || null;

      if (Object.keys(filesToUpload).length > 0) {
         const imagesPayload = await FileUtils.buildImagesPayload(filesToUpload);

         const folderHuman = [formData.driverFirstName || "", formData.driverLastName || ""]
            .filter(Boolean)
            .join(" ");

         try {
            const uploadResp = await api.post("/api/upload-images/", {
               folder_name: folderHuman || undefined,
               images: imagesPayload,
            });
            if (uploadResp.data) {
               const { truckPhoto, cabCard } = uploadResp.data || {};
               if (truckPhoto) truckPhotoUrl = truckPhoto;
               if (cabCard) cabCardUrl = cabCard;
            }
         } catch (error) {
            console.error("Upload failed:", error);
            throw new Error("Failed to upload images. Please try again.");
         }
      }

      // Build payload as FormData (backend expects form fields)
      const payload = new FormData();
      Object.keys(formData).forEach((key) => {
         // Skip raw File objects
         if (formData[key] instanceof File) return;
         if (formData[key] !== null && formData[key] !== undefined) {
            payload.append(key, formData[key]);
         }
      });

      // Append resolved image URLs
      if (truckPhotoUrl) payload.append("truckPhotoUrl", truckPhotoUrl);
      if (cabCardUrl) payload.append("cabCardUrl", cabCardUrl);

      const response = await apiWithAuth.post("/driver-form/submit", payload, {
         headers: { "Content-Type": "multipart/form-data" },
      });

      return response.data;
   },

   // Get driver verification responses
   getResponses: async () => {
      const response = await api.get("/api/driver-form/responses");
      return response.data;
   },

   // Clear driver verification responses
   clearResponses: async () => {
      const response = await api.delete("/api/driver-form/responses");
      return response.data;
   },
};

// Forms Dashboard APIs
export const formsAPI = {
   // Get all forms
   getForms: async (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.form_type) params.append("form_type", filters.form_type);
      if (filters.conversation_id) params.append("conversation_id", filters.conversation_id);
      if (filters.error_status) params.append("error_status", filters.error_status);
      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);

      const response = await api.get(`/api/forms?${params.toString()}`);
      return response.data;
   },

   // Get form by ID
   getFormById: async (formId) => {
      const response = await api.get(`/api/forms/${formId}`);
      return response.data;
   },

   // Submit error report
   submitErrorReport: async (formId, formData) => {
      try {
         const response = await fetch(`/api/forms/report/${formId}/submit`, {
            method: "POST",
            body: formData, // FormData will automatically set the correct Content-Type
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to submit error report");
         }

         const data = await response.json();
         return {
            success: true,
            data: data.data,
         };
      } catch (error) {
         console.error("Error submitting error report:", error);
         return {
            success: false,
            error: error.message || "Failed to submit error report",
         };
      }
   },

   // Get error reports for a form
   getErrorReports: async (formId) => {
      try {
         const response = await api.get(`/api/forms/${formId}/errors`);
         console.log("Raw API Response:", response); // Debug log

         // Ensure we're returning the correct data structure
         if (response.data.success) {
            return {
               success: true,
               data: {
                  error_reports: response.data.error_reports || [],
                  form_submission: response.data.form_submission || null,
               },
            };
         } else {
            return {
               success: false,
               error: response.data.error || "Failed to fetch error reports",
            };
         }
      } catch (error) {
         console.error("API Error:", error); // Debug log
         return {
            success: false,
            error: "Failed to fetch error reports",
         };
      }
   },

   // Resolve error report
   resolveErrorReport: async (formId, reportId, resolutionDescription) => {
      const formData = new FormData();
      formData.append("resolution_description", resolutionDescription);

      const response = await api.post(`/forms/${formId}/errors/${reportId}/resolve`, formData, {
         headers: {
            "Content-Type": "multipart/form-data",
         },
      });
      return response.data;
   },
};

// Global Dashboard APIs
export const dashboardAPI = {
   // Get dashboard data
   getDashboardData: async (resolutionFilter = null) => {
      const params = resolutionFilter ? `?resolution_filter=${resolutionFilter}` : "";
      const response = await api.get(`/api/dashboard${params}`);
      return response.data;
   },

   // Resolve error report from dashboard
   resolveErrorReport: async (reportId) => {
      const response = await api.post(`/dashboard/error-report/${reportId}/resolve`);
      return response.data;
   },

   // Unresolve error report from dashboard
   unresolveErrorReport: async (reportId) => {
      const response = await api.post(`/dashboard/error-report/${reportId}/unresolve`);
      return response.data;
   },
};

// Authentication is now handled by Supabase in the frontend
// No backend authentication endpoints needed

// Error Reports
export const getErrorReports = async (formId) => {
   try {
      const response = await fetch(`/forms/${formId}/errors`, {
         method: "GET",
         headers: {
            "Content-Type": "application/json",
         },
      });

      const data = await response.json();
      return {
         success: response.ok,
         data: data.error_reports,
         error: !response.ok ? data.error : null,
      };
   } catch (error) {
      console.error("Error fetching error reports:", error);
      return {
         success: false,
         error: "Failed to fetch error reports",
      };
   }
};

export const resolveErrorReport = async (formId, reportId, resolutionDescription) => {
   try {
      const response = await fetch(`/forms/${formId}/errors/${reportId}/resolve`, {
         method: "POST",
         headers: {
            "Content-Type": "application/json",
         },
         body: JSON.stringify({ resolution_description: resolutionDescription }),
      });

      const data = await response.json();
      return {
         success: response.ok,
         data: data,
         error: !response.ok ? data.error : null,
      };
   } catch (error) {
      console.error("Error resolving error report:", error);
      return {
         success: false,
         error: "Failed to resolve error report",
      };
   }
};

export default api;
